import React from "react";
import { ClientAuthProvider, useClientAuth } from "./ClientAuthContext";
import type { ClientView, PendingProof } from "./types";
import ClientLoginView from "./views/ClientLoginView";
import ClientSignupView from "./views/ClientSignupView";
import ClientAppointmentsView from "./views/ClientAppointmentsView";
import ClientOtpView from "./views/ClientOtpView";

function ClientDashboardFlowInner({ onClose }: { onClose: () => void }) {
    const { state } = useClientAuth();
    const [view, setView] = React.useState<ClientView>(() => (state.isLoggedIn ? "dashboard" : "login"));
    // The form waiting for its SMS code; kept after "back" so the form
    // reopens filled in and only the number needs fixing.
    const [pending, setPending] = React.useState<PendingProof | null>(null);

    // A confirmed code logs the client in — that is the only way in.
    React.useEffect(() => {
        if (state.isLoggedIn && view !== "dashboard") {
            setPending(null);
            setView("dashboard");
        }
    }, [state.isLoggedIn, view]);

    const askForCode = (proof: PendingProof) => {
        setPending(proof);
        setView("otp");
    };

    if (view === "otp" && pending) {
        return (
            <ClientOtpView
                pending={pending}
                onClose={onClose}
                onBack={() => setView(pending.from)}
            />
        );
    }

    if (view === "signup") {
        return (
            <ClientSignupView
                initial={pending?.from === "signup" ? pending : undefined}
                onClose={onClose}
                onSwitchToLogin={() => setView("login")}
                onCodeRequired={askForCode}
            />
        );
    }

    if (view === "dashboard" && state.isLoggedIn) {
        return <ClientAppointmentsView onClose={onClose} />;
    }

    return (
        <ClientLoginView
            initialPhone={pending?.phone}
            onClose={onClose}
            onSwitchToSignup={() => setView("signup")}
            onCodeRequired={askForCode}
        />
    );
}

export default function ClientDashboardFlow({ onClose }: { onClose: () => void }) {
    return (
        <ClientAuthProvider>
            <ClientDashboardFlowInner onClose={onClose} />
        </ClientAuthProvider>
    );
}
